import * as THREE from 'three';
import { CadifyError, ErrorTypes } from './errors.js';

// Default material for shapes
export const createDefaultMaterial = (color = 0x9bbcff) => {
  return new THREE.MeshStandardMaterial({
    color,
    metalness: 0.2,
    roughness: 0.6,
    side: THREE.DoubleSide
  });
};

// Shape generators
export const ShapeGenerators = {
  box: (params = {}) => {
    const {
      width = 1,
      height = 1,
      depth = 1,
      widthSegments = 1,
      heightSegments = 1,
      depthSegments = 1,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.BoxGeometry(
      width, height, depth,
      widthSegments, heightSegments, depthSegments
    );
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'box' };
  },
  
  sphere: (params = {}) => {
    const {
      radius = 0.5,
      widthSegments = 32,
      heightSegments = 16,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.SphereGeometry(
      radius, widthSegments, heightSegments
    );
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'sphere' };
  },
  
  cylinder: (params = {}) => {
    const {
      radiusTop = 0.5,
      radiusBottom = 0.5,
      height = 1,
      radialSegments = 32,
      heightSegments = 1,
      openEnded = false,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.CylinderGeometry(
      radiusTop, radiusBottom, height,
      radialSegments, heightSegments, openEnded
    );
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'cylinder' };
  },
  
  cone: (params = {}) => {
    const {
      radius = 0.5,
      height = 1,
      radialSegments = 32,
      heightSegments = 1,
      openEnded = false,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.ConeGeometry(
      radius, height, radialSegments, heightSegments, openEnded
    );
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'cone' };
  },
  
  torus: (params = {}) => {
    const {
      radius = 0.5,
      tube = 0.2,
      radialSegments = 16,
      tubularSegments = 100,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.TorusGeometry(
      radius, tube, radialSegments, tubularSegments
    );
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'torus' };
  },
  
  plane: (params = {}) => {
    const {
      width = 1,
      height = 1,
      widthSegments = 1,
      heightSegments = 1,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.PlaneGeometry(
      width, height, widthSegments, heightSegments
    );
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'plane' };
  },
  
  circle: (params = {}) => {
    const {
      radius = 0.5,
      segments = 32,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.CircleGeometry(radius, segments);
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'circle' };
  },
  
  ring: (params = {}) => {
    const {
      innerRadius = 0.3,
      outerRadius = 0.5,
      thetaSegments = 32,
      phiSegments = 1,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.RingGeometry(
      innerRadius, outerRadius, thetaSegments, phiSegments
    );
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'ring' };
  },
  
  tetrahedron: (params = {}) => {
    const {
      radius = 0.5,
      detail = 0,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.TetrahedronGeometry(radius, detail);
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'tetrahedron' };
  },
  
  octahedron: (params = {}) => {
    const {
      radius = 0.5,
      detail = 0,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.OctahedronGeometry(radius, detail);
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'octahedron' };
  },
  
  dodecahedron: (params = {}) => {
    const {
      radius = 0.5,
      detail = 0,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.DodecahedronGeometry(radius, detail);
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'dodecahedron' };
  },
  
  icosahedron: (params = {}) => {
    const {
      radius = 0.5,
      detail = 0,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.IcosahedronGeometry(radius, detail);
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'icosahedron' };
  },
  
  torusKnot: (params = {}) => {
    const {
      radius = 0.5,
      tube = 0.15,
      tubularSegments = 64,
      radialSegments = 8,
      p = 2,
      q = 3,
      color = 0x9bbcff
    } = params;
    
    const geometry = new THREE.TorusKnotGeometry(
      radius, tube, tubularSegments, radialSegments, p, q
    );
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'torusKnot' };
  }
};

// Generate 3D text
export const generateText3D = async (text, params = {}) => {
  const {
    size = 0.5,
    height = 0.1,
    curveSegments = 12,
    bevelEnabled = true,
    bevelThickness = 0.02,
    bevelSize = 0.01,
    bevelSegments = 3,
    color = 0x9bbcff,
    font = null
  } = params;
  
  if (!font) {
    throw new CadifyError('Font is required for text generation', ErrorTypes.VALIDATION);
  }
  
  try {
    const shapes = font.generateShapes(text, size);
    const geometry = new THREE.ExtrudeGeometry(shapes, {
      depth: height,
      bevelEnabled,
      bevelThickness,
      bevelSize,
      bevelSegments,
      curveSegments
    });
    
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.center();
    
    const material = createDefaultMaterial(color);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return { mesh, geometry, type: 'text3d' };
  } catch (error) {
    throw new CadifyError('Failed to generate 3D text', ErrorTypes.GEOMETRY, error);
  }
};

// Utility functions
export const centerGeometry = (geometry) => {
  geometry.computeBoundingBox();
  const center = geometry.boundingBox.getCenter(new THREE.Vector3());
  geometry.translate(-center.x, -center.y, -center.z);
  return geometry;
};

export const getGeometryStats = (object) => {
  const box = new THREE.Box3().setFromObject(object);
  const size = box.getSize(new THREE.Vector3());
  
  let triangles = 0;
  let vertices = 0;
  
  object.traverse((obj) => {
    if (obj.isMesh && obj.geometry) {
      const geo = obj.geometry;
      const count = geo.index ? geo.index.count : geo.attributes.position.count;
      triangles += Math.floor(count / 3);
      vertices += geo.attributes.position.count;
    }
  });
  
  return {
    triangles,
    vertices,
    size: { x: size.x, y: size.y, z: size.z },
    volume: size.x * size.y * size.z
  };
};

